/**
 * Permisjoner i PowerOffice (#183 phase 3): leave registered in PowerOffice Go that has not ended, for
 * this store's people (GET /admin/poweroffice/leaves), so nobody on leave is planned. Read on a press,
 * one call per employment in Go. Nothing is written, in Timefly or in PowerOffice.
 */
import { errorText, get } from '@/api/client';
import { T } from '@/i18n';
import { Button } from '@/ui';
import { fmtD } from '@/ui/format';
import { useQuery } from '@tanstack/react-query';
import { POWEROFFICE, type PayrollSystem } from './payrollSystem';

interface Leave {
  user_id: number;
  name: string;
  number: string;
  type: string;
  from: string | null;
  to: string | null;
  percent: number | null;
}

export function PowerOfficeLeaves({
  onClose,
  sys = POWEROFFICE,
}: { onClose: () => void; sys?: PayrollSystem }) {
  const q = useQuery({
    queryKey: [sys.key, 'leaves'],
    queryFn: async () => (await get<{ leaves: Leave[] }>(`${sys.base}/leaves`)).leaves,
    staleTime: 0,
  });
  const leaves = q.data ?? [];
  return (
    <section data-testid="po-leaves" style={{ display: 'grid', gap: 10, marginTop: 6 }}>
      <b style={{ fontSize: 15 }}>{T('Permisjoner i {system}', { system: sys.short })}</b>
      {q.isPending ? (
        <output style={{ color: 'var(--ink3)' }}>
          {T('Henter permisjoner fra {system} …', { system: sys.short })}
        </output>
      ) : q.isError ? (
        <span>{errorText(q.error)}</span>
      ) : leaves.length === 0 ? (
        <span style={{ color: 'var(--ink3)', fontSize: 14 }}>
          {T('Ingen har permisjon nå eller fremover.')}
        </span>
      ) : (
        <table className="t">
          <thead>
            <tr>
              <th>{T('Ansatt')}</th>
              <th>{T('Permisjon')}</th>
              <th>{T('Fra')}</th>
              <th>{T('Til')}</th>
              <th>{T('Andel')}</th>
            </tr>
          </thead>
          <tbody>
            {leaves.map((l, i) => (
              <tr key={`${l.user_id}-${i}`}>
                <td>
                  {l.name} ({l.number})
                </td>
                <td>{T(l.type)}</td>
                <td>{l.from ? fmtD(l.from) : '–'}</td>
                <td>{l.to ? fmtD(l.to) : T('Ikke satt')}</td>
                <td>{l.percent == null ? '–' : `${l.percent} %`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <span>
        <Button onClick={onClose}>{T('Lukk')}</Button>
      </span>
    </section>
  );
}
